import { useCart } from '../../hooks/useCart';

export const CartDrawer = () => {
  const { cart, isLoading, isOpen, closeCart, updateLineQuantity, removeLine } = useCart();

  if (!isOpen) return null;

  const handleQuantityChange = (lineId: string, newQuantity: number, availableQuantity: number) => {
    if (newQuantity < 1) {
      removeLine(lineId);
    } else if (newQuantity <= availableQuantity) {
      updateLineQuantity(lineId, newQuantity);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[400px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Shopping Cart</h2>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
            aria-label="Close cart"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!cart || cart.lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-600 mb-2">Your cart is empty</p>
              <button
                onClick={closeCart}
                className="text-teal-600 hover:text-teal-700 font-medium transition-colors whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.lines.map((line) => (
                <div key={line.id} className="flex gap-4 pb-4 border-b border-gray-100">
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                    {line.image ? (
                      <img
                        src={line.image}
                        alt={line.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-2xl text-gray-400"></i>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                      {line.title}
                    </h3>
                    {line.variantTitle && (
                      <p className="text-xs text-gray-500 mb-2">{line.variantTitle}</p>
                    )}
                    <p className="font-semibold text-gray-900 text-sm">{line.price}</p>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-gray-300 rounded-lg">
                        <button
                          onClick={() => handleQuantityChange(line.id, line.quantity - 1, line.availableQuantity)}
                          disabled={isLoading}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-10 text-center text-sm font-medium text-gray-900">
                          {line.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(line.id, line.quantity + 1, line.availableQuantity)}
                          disabled={isLoading || line.quantity >= line.availableQuantity}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
                          aria-label="Increase quantity"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>

                      <button
                        onClick={() => removeLine(line.id)}
                        disabled={isLoading}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                        aria-label="Remove item"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>

                    {line.quantity >= line.availableQuantity && line.availableQuantity > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        Only {line.availableQuantity} available
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart && cart.lines.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-xl font-bold text-gray-900">{cart.subtotal}</span>
            </div>

            <p className="text-xs text-gray-500">
              Shipping and taxes calculated at checkout
            </p>

            {/* Checkout Button */}
            <a
              href={cart.checkoutUrl}
              className="block w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors whitespace-nowrap"
            >
              Proceed to Checkout
            </a>

            <button
              onClick={closeCart}
              className="block w-full text-gray-600 hover:text-gray-900 font-medium py-2 text-center transition-colors whitespace-nowrap"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
};